import SparkMD5 from 'spark-md5';

import type { HtmlEditApplierDeps, HtmlEditApplierMode, ParagraphInfo } from './shared';
import { documentIdSchema, getIsCancellationError, stripSelectionMarkers } from './shared';
import { getIsPdfLocationAnchor } from '../../extract-paragraphs/pdf/locationAnchor';
import { AlineTransforms, applyInsertOutput, generateSuggestedEditGroupId } from '../../slate-api';
import { buildInsertAnchorPreview } from '../insert-anchor-preview';
import type { InsertSuggestion } from '../redline-llm-output';
import type {
  CodeBlockRedlineEditError,
  CodeBlockRedlineInsert,
  CodeBlockRedlineItem,
} from '../redline-shapes';

/**
 * Apply one streamed `insert` suggestion to the server-side editor and
 * convert it into the `CodeBlockRedlineItem` (either a `redline-insert` or a
 * `redline-edit-error`) that the client will render. The whole insertion is
 * applied as a single suggestion group: the sourced suggested edits carrying
 * the groupId ARE the suggestion — no marker pair is created.
 */
export async function applyInsertSuggestion(
  suggest: InsertSuggestion,
  deps: HtmlEditApplierDeps,
  mode: HtmlEditApplierMode
): Promise<CodeBlockRedlineItem> {
  const { editorPool, errorReporter, logger, makeSource, paragraphState, userId } = deps;
  const { documentId, id: paragraphId, position, reasoning } = suggest;
  const html = stripSelectionMarkers(suggest.html);
  const groupId = generateSuggestedEditGroupId();

  const insertError = (message: string): CodeBlockRedlineEditError => ({
    documentId,
    groupId,
    locationAnchor: paragraphId,
    message,
    type: 'redline-edit-error',
  });

  /**
   * The insertion's dependency fingerprint is its anchor paragraph's text —
   * a consumer deferring materialization can compare it to detect that the
   * content the insertion was written against has changed. The same text also
   * rides along trimmed for display, so the cards can show what the insertion
   * is anchored to.
   */
  const buildInsertItem = (anchorText: string): CodeBlockRedlineInsert => ({
    anchorPreview: buildInsertAnchorPreview({ position, text: anchorText }),
    documentId,
    groupId,
    html,
    locationAnchor: paragraphId,
    position,
    reasoning,
    textHash: SparkMD5.hash(anchorText),
    type: 'redline-insert',
  });

  /**
   * The model supplies `documentId` as a loose string, so it can arrive empty
   * or as an invented placeholder when no document is attached. An
   * unresolvable target is a per-edit problem, not a turn failure, so report
   * it as a `redline-edit-error` and let the rest of the stream proceed.
   */
  if (!documentIdSchema.safeParse(documentId).success) {
    const message = `RedlineAgent emitted an invalid documentId "${documentId}" for paragraph "${paragraphId}".`;
    logger.warn(message);
    return insertError(message);
  }

  /**
   * Validate the anchor up front (and lazily load the document's paragraph
   * state) so a bad anchor produces the same rich error reporting as the
   * edit-paragraph path instead of a throw from the transform.
   */
  let paragraphInfo: ParagraphInfo | undefined;
  try {
    paragraphInfo = await paragraphState.getParagraphInfoByLocationAnchorWithLazyLoad(
      documentId,
      paragraphId
    );
  } catch (error) {
    /**
     * The lazy load throws for a valid-looking but unresolvable documentId,
     * such as a deleted document. That is one failed insertion rather than a
     * failed node. A user Stop must still propagate, so re-throw cancellation
     * before converting anything to an edit error.
     */
    if (getIsCancellationError(error)) throw error;
    const message = `RedlineAgent could not load document "${documentId}" for paragraph "${paragraphId}": ${error instanceof Error ? error.message : String(error)}`;
    logger.warn(message);
    errorReporter.captureMessage(message, {
      extra: { documentId, paragraphId, position },
      level: 'warning',
    });
    return insertError(message);
  }
  if (!paragraphInfo) {
    const message = `RedlineAgent could not find paragraph "${paragraphId}" in document "${documentId}" to insert ${position}.`;
    logger.error(message);
    const allAnchors = paragraphState.getAllLocationAnchorsForDocument(documentId);
    errorReporter.captureMessage(message, {
      extra: {
        availableAnchorsCount: allAnchors.length,
        availableAnchorsSample: allAnchors.slice(0, 50),
        documentId,
        paragraphId,
        position,
      },
      level: 'error',
    });
    return insertError(message);
  }

  /**
   * Preview mode returns the card without applying. PDF (Apryse) documents
   * are read-only — their coordinate-based paragraph anchors don't exist in
   * the collab editor, so applying would throw — and signing-locked documents
   * (sent for signature or signed) must not receive new edits: both always
   * take this path.
   */
  if (
    mode === 'preview' ||
    getIsPdfLocationAnchor(paragraphId) ||
    (await editorPool.getSigningLockState(documentId))
  ) {
    return buildInsertItem(paragraphInfo.text);
  }

  try {
    const editor = await editorPool.getOrCreateEditor(documentId);
    AlineTransforms.withoutNormalizing(editor, () => {
      applyInsertOutput(editor, {
        authorId: userId,
        groupId,
        html,
        locationAnchor: paragraphId,
        position,
        reasoning,
        source: makeSource(groupId),
      });
    });
  } catch (error) {
    const message =
      error instanceof Error
        ? `RedlineAgent applyInsertOutput threw for paragraph "${paragraphId}": ${error.message}`
        : `RedlineAgent applyInsertOutput threw for paragraph "${paragraphId}".`;
    logger.error(message, error);
    errorReporter.captureException(error, { extra: { documentId, paragraphId, position } });
    return insertError(message);
  }

  return buildInsertItem(paragraphInfo.text);
}
